// Books setup's words and shapes (accounts.setupStatus, accounts.list, bankAccounts.list), shared by
// Riverstone's Setup.tsx and Blueprint's BooksSetupPage.tsx: the four steps and what each unlocks, the
// chart's types and the template's groups, the months, and whether a step is done.

export interface Role {
  role: string;
  key: string;
  label: string;
  tech: string;
  blocks: string;
  main: boolean;
  types: string[];
  accountId: string | null;
}
export interface Status {
  template: { code: string; name: string; subtype: string }[];
  accounts: number;
  roles: Role[];
  waiting: { total: number; invoices: { id: string; number: string }[]; invoiceCount: number };
  posted: boolean;
  fiscalYearStartMonth: number;
  fiscalYearSet: boolean;
  canEdit: boolean;
}
export interface Account {
  id: string;
  code: string;
  name: string;
  type: string;
  subtype: string;
  active: boolean;
  rowVersion: number;
}
export interface BankAccount {
  id: string;
  name: string;
  accountId: string;
  institution: string | null;
  last4: string | null;
}

export type Step = 'chart' | 'roles' | 'bank' | 'fy' | 'ready';
export const STEPS: { id: Exclude<Step, 'ready'>; name: string; unlocks: string }[] = [
  { id: 'chart', name: 'Chart of accounts', unlocks: 'Posting rules know where money goes' },
  { id: 'roles', name: 'Account roles', unlocks: 'Posting rules can post invoices and payments' },
  { id: 'bank', name: 'Bank accounts', unlocks: 'Banking can match lines' },
  { id: 'fy', name: 'Fiscal year', unlocks: 'Reports and year-end run on your year' },
];

export const SUBTYPE_LABEL: Record<string, string> = {
  bank: 'Bank',
  current_asset: 'Current asset',
  inventory: 'Inventory',
  fixed_asset: 'Fixed asset',
  current_liability: 'Current liability',
  credit_card: 'Credit card',
  long_term_liability: 'Long-term liability',
  equity: 'Equity',
  income: 'Income',
  other_income: 'Other income',
  cost_of_sales: 'Cost of jobs',
  expense: 'Running cost',
};
export const GROUPS: [string, string[]][] = [
  ['Bank and money in', ['bank', 'current_asset']],
  ['Stock and equipment', ['inventory', 'fixed_asset']],
  ['What you owe', ['current_liability', 'credit_card', 'long_term_liability']],
  ['Owner’s equity', ['equity']],
  ['Income', ['income', 'other_income']],
  ['Costs of jobs', ['cost_of_sales']],
  ['Running costs', ['expense']],
];
export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** The fiscal year that holds today, e.g. "Sep 2026 – Aug 2027". */
export function fiscalYearText(startMonth: number, today = new Date()): string {
  const y = today.getMonth() + 1 >= startMonth ? today.getFullYear() : today.getFullYear() - 1;
  const endMonth = ((startMonth + 10) % 12) + 1;
  const endYear = startMonth === 1 ? y : y + 1;
  return `${MONTHS[startMonth - 1]!.slice(0, 3)} ${y} – ${MONTHS[endMonth - 1]!.slice(0, 3)} ${endYear}`;
}
export const roleKey = (r: Pick<Role, 'role' | 'key'>) => `${r.role}|${r.key}`;
export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Whether a step is done: a chart, every role with an account, a bank account, the year chosen (or fixed
 * by the first posting: it can't change after that). */
export function stepDone(st: Exclude<Step, 'ready'>, s: Status, bankCount: number): boolean {
  if (st === 'chart') return s.accounts > 0;
  if (st === 'roles') return s.accounts > 0 && s.roles.every((r) => r.accountId);
  if (st === 'bank') return bankCount > 0;
  return s.fiscalYearSet || s.posted;
}
